import { saveCallbackStatus } from '@finpay-development/shared-components';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LevelOfCare } from '../models/level-of-care';
import { PayerRow } from '../models/payers';
import {
  getBlankRule,
  getBlankRuleCriteriaParam,
  getDefaultRuleEdit,
  Rule,
  RuleCriteriaParam,
  RuleEditViewModel,
  RuleSet,
} from '../models/rule';
import { Scholarship } from '../models/scholarship';
import { State } from '../../shared/model/state';
import { ConfigRiskThreshold } from '../models/risk-threshold'
import { ConfigRiskClassSetting, FinPayRiskClass } from '../models/risk-class-setting';

interface AdminContextState {
  rules: RuleSet;
  allRules: Rule[];
  currentRule: RuleEditViewModel;
  fullRule: Rule;
  currentRuleCriteriaParam: RuleCriteriaParam;
  allRuleCriteriaParams: RuleCriteriaParam[];
  usStates: State[],
  levelsOfCare: LevelOfCare[],
  payers: PayerRow[]
  payersMap: Map<number, PayerRow>
  modalSaveStatus: saveCallbackStatus;
  modalDeleteStatus: saveCallbackStatus;
  errorMessage: string;
  isSaveSuccessful: boolean;
  isLoading: {
    isLoadingRules: boolean;
    isLoadingPayer: boolean;
    isLoadingUsStates: boolean;
    isLoadingLevelsOfCare: boolean,
    getLevelsOfCareStatus: saveCallbackStatus,
    isLoadingScholarship: boolean,
    getScholarshipsStatus: saveCallbackStatus,
    isLoadingRiskThresholds: boolean,
    getRiskThresholdsStatus: saveCallbackStatus,
    isLoadingRiskClassSettings: boolean,
    getRiskClassSettingsStatus: saveCallbackStatus,
    isLoadingFinPayRiskClasses: boolean,
  },
  isError: {
    payer: boolean
  },
  scholarships: Scholarship[],
  riskThresholds: ConfigRiskThreshold[],
  riskClassSettings: ConfigRiskClassSetting[],
  finPayRiskClasses: FinPayRiskClass[],
}

const initialState = {
  rules: [],
  allRules: [],
  currentRule: getDefaultRuleEdit(),
  fullRule: getBlankRule(),
  currentRuleCriteriaParam: getBlankRuleCriteriaParam(),
  allRuleCriteriaParams: [],
  usStates: [],
  levelsOfCare: [],
  payers: [],
  payersMap:  new Map(),
  modalSaveStatus: saveCallbackStatus.none,
  modalDeleteStatus: saveCallbackStatus.none,
  errorMessage: "",
  isSaveSuccessful: true,
  isLoading: {
    isLoadingRules: false,
    isLoadingPayer: true,
    isLoadingUsStates: false,
    isLoadingLevelsOfCare: false,
    getLevelsOfCareStatus: saveCallbackStatus.none,
    isLoadingScholarship: false,
    getScholarshipsStatus: saveCallbackStatus.none,
    isLoadingRiskThresholds: false,
    getRiskThresholdsStatus: saveCallbackStatus.none,
    isLoadingRiskClassSettings: false,
    getRiskClassSettingsStatus: saveCallbackStatus.none,
    isLoadingFinPayRiskClasses: false,
  },
  isError: {
    payer: false
  },
  scholarships: [],
  riskThresholds: [],
  riskClassSettings: [],
  finPayRiskClasses: [],

} as AdminContextState;

const adminConfigurationSlice = createSlice({
  name: "adminConfigurationContext",
  initialState,
  reducers: {
    clearStatus: (state) => {
      state.modalSaveStatus = saveCallbackStatus.none;
      state.modalDeleteStatus = saveCallbackStatus.none;
      state.errorMessage = "";
      state.isLoading.getLevelsOfCareStatus = saveCallbackStatus.none;
      state.isSaveSuccessful = true;
      state.isLoading.getScholarshipsStatus = saveCallbackStatus.none;
      state.isLoading.getRiskThresholdsStatus = saveCallbackStatus.none;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      // may not be needed. (unused currently). re-evaluate later
      state.isLoading.isLoadingRules = action.payload;
    },
    resetAdminConfigurationContext() {
      return initialState;
    },
  },
});

export const {
  resetAdminConfigurationContext,
} = adminConfigurationSlice.actions;

export default adminConfigurationSlice.reducer;
